'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// HQ — Kettering, OH (used for the hero weather widget)
const HQ_LAT = 39.6895;
const HQ_LON = -84.1688;

interface HqWeather {
  temperatureF: number;
  windMph: number;
  code: number;
  label: string;
  emoji: string;
}

// Open-Meteo WMO weather code → human label + emoji.
// https://open-meteo.com/en/docs (current weather codes section)
function labelForCode(code: number): { label: string; emoji: string } {
  if (code === 0) return { label: 'Clear', emoji: '☀️' };
  if (code <= 3) return { label: 'Partly cloudy', emoji: '⛅' };
  if (code <= 48) return { label: 'Fog', emoji: '🌫️' };
  if (code <= 57) return { label: 'Drizzle', emoji: '🌦️' };
  if (code <= 67) return { label: 'Rain', emoji: '🌧️' };
  if (code <= 77) return { label: 'Snow', emoji: '❄️' };
  if (code <= 82) return { label: 'Showers', emoji: '🌧️' };
  if (code <= 86) return { label: 'Snow showers', emoji: '🌨️' };
  if (code <= 99) return { label: 'Thunderstorm', emoji: '⛈️' };
  return { label: '—', emoji: '·' };
}

interface Row1HeroProps {
  displayName: string;
  escalationsCount: number;
  activeInstallsCount: number;
  aiSummary: string | null;
  aiLoading: boolean;
}

export default function Row1Hero({
  displayName,
  escalationsCount,
  activeInstallsCount,
  aiSummary,
  aiLoading,
}: Row1HeroProps) {
  const [now, setNow] = useState<Date | null>(null);
  const [weather, setWeather] = useState<HqWeather | null>(null);
  const [weatherError, setWeatherError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => { setNow(new Date()); }, []);

  // Fetch HQ weather from Open-Meteo (no key required).
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${HQ_LAT}&longitude=${HQ_LON}` +
      `&current=temperature_2m,weather_code,wind_speed_10m` +
      `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/New_York`;

    fetch(url, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const code = Number(data?.current?.weather_code ?? -1);
        const { label, emoji } = labelForCode(code);
        setWeather({
          temperatureF: Math.round(Number(data?.current?.temperature_2m ?? 0)),
          windMph: Math.round(Number(data?.current?.wind_speed_10m ?? 0)),
          code,
          label,
          emoji,
        });
      })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        console.warn('[Row1Hero] weather fetch failed:', err);
        if (!cancelled) setWeatherError(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // System status: 0 escalations = GREEN, 1–3 = YELLOW, 4+ = RED.
  const systemStatus =
    escalationsCount === 0 ? 'GREEN'
    : escalationsCount <= 3 ? 'YELLOW'
    : 'RED';
  const statusBg =
    systemStatus === 'GREEN' ? 'rgba(16,185,129,0.12)'
    : systemStatus === 'YELLOW' ? 'rgba(245,158,11,0.12)'
    : 'rgba(239,68,68,0.12)';
  const statusFg =
    systemStatus === 'GREEN' ? '#10B981'
    : systemStatus === 'YELLOW' ? '#F59E0B'
    : '#EF4444';

  const dateLine = now
    ? now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '—';

  return (
    <section className="space-y-4">
      {/* ── Hero strip: date + weather + status (left) | revenue (right) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: Date / weather / status */}
        <div
          className="lg:col-span-2 rounded-xl p-5"
          style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-1">Today</p>
          <h2
            className="text-lg font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            {dateLine}
          </h2>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              {weather ? (
                <>
                  <span className="text-xl leading-none">{weather.emoji}</span>
                  <span className="font-semibold">{weather.temperatureF}°F</span>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-600">{weather.label}</span>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-600">{weather.windMph} mph wind</span>
                </>
              ) : weatherError ? (
                <span className="text-xs text-gray-400">HQ weather unavailable</span>
              ) : (
                <span className="text-xs text-gray-400">Loading HQ weather…</span>
              )}
            </div>
            <span
              className="ml-auto rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide"
              style={{ background: statusBg, color: statusFg }}
            >
              ● System {systemStatus}
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">HQ: Kettering, OH</p>
        </div>

        {/* Right: Revenue Today (mock) */}
        <div
          className="lg:col-span-3 relative rounded-xl p-5"
          style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
        >
          <DemoDataPill />
          <p className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-3">Revenue today</p>
          <div className="grid grid-cols-3 gap-4">
            <RevenueStat label="Goal" value="$0" delta="" tone="neutral" />
            <RevenueStat label="Collected" value="$0" delta="" tone="neutral" />
            <RevenueStat label="A/R outstanding" value="$0" delta="" tone="neutral" />
          </div>
        </div>
      </div>

      {/* ── SmartPick AI Daily Summary (moved here from previous layout) ── */}
      <div
        className="relative rounded-xl p-6 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)',
          boxShadow: '0 4px 20px rgba(7,176,233,0.25)',
        }}
      >
        <div
          className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'white', transform: 'translate(30%, -30%)' }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              <Icon name="SparklesIcon" size={16} className="text-white" />
            </div>
            <span className="text-white/80 text-xs font-semibold tracking-widest uppercase">
              SmartPick™ AI Daily Summary
            </span>
            <span
              className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ background: statusBg, color: statusFg }}
            >
              ● System {systemStatus}
            </span>
          </div>
          {aiLoading && !aiSummary ? (
            <p className="text-white/70 text-sm">Generating today's briefing…</p>
          ) : aiSummary ? (
            <p
              className="text-white text-sm leading-relaxed"
              style={{ fontFamily: 'Inter, Poppins, sans-serif' }}
            >
              {aiSummary}
            </p>
          ) : (
            <p
              className="text-white text-sm leading-relaxed"
              style={{ fontFamily: 'Inter, Poppins, sans-serif' }}
            >
              Good morning, <span className="font-bold">{displayName}</span>.{' '}
              <span className="font-bold">{activeInstallsCount}</span> active installs today.{' '}
              <span className="font-bold">{escalationsCount}</span> open escalations.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

interface RevenueStatProps {
  label: string;
  value: string;
  delta: string;
  tone: 'up' | 'down' | 'neutral';
}

function RevenueStat({ label, value, delta, tone }: RevenueStatProps) {
  const deltaColor = tone === 'up' ? '#10B981' : tone === 'down' ? '#EF4444' : '#6B7280';
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
      <p
        className="text-2xl font-bold mt-0.5"
        style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
      >
        {value}
      </p>
      {delta && (
        <p className="text-xs font-medium mt-0.5" style={{ color: deltaColor }}>
          {delta}
        </p>
      )}
    </div>
  );
}
